/**
 * @file
 * Equalize direct grid column heights for .make-even-height containers.
 *
 * Only runs when the grid has multiple columns in one row (md:grid-cols-*).
 * On mobile (single column stack), min-heights are cleared so cards flow naturally.
 */
(function (Drupal, once) {
  'use strict';

  const GRID_COLUMN_SELECTOR = ':scope > .grid > .min-w-0';

  /**
   * @param {Element} grid
   * @returns {number}
   */
  function getGridColumnCount(grid) {
    const template = window.getComputedStyle(grid).gridTemplateColumns;
    if (!template || template === 'none') {
      return 1;
    }
    return template.split(' ').filter((part) => part.trim() !== '').length;
  }

  /**
   * @param {Element[]} items
   */
  function resetHeights(items) {
    items.forEach((item) => {
      item.style.minHeight = '';
    });
  }

  /**
   * @param {Element} container
   */
  function equalize(container) {
    const grid = container.querySelector(':scope > .grid');
    if (!grid) {
      return;
    }

    const items = Array.from(grid.querySelectorAll(GRID_COLUMN_SELECTOR));
    if (items.length < 2) {
      return;
    }

    if (getGridColumnCount(grid) <= 1) {
      resetHeights(items);
      return;
    }

    resetHeights(items);

    const maxHeight = items.reduce(
      (tallest, item) => Math.max(tallest, item.getBoundingClientRect().height),
      0,
    );

    if (maxHeight <= 0) {
      return;
    }

    const minHeight = `${Math.ceil(maxHeight)}px`;
    items.forEach((item) => {
      if (item.style.minHeight !== minHeight) {
        item.style.minHeight = minHeight;
      }
    });

    // Re-measure once flex layout has applied inside .make-even-height.
    window.requestAnimationFrame(() => {
      const adjustedMax = items.reduce(
        (tallest, item) => Math.max(tallest, item.getBoundingClientRect().height),
        0,
      );
      if (adjustedMax > maxHeight) {
        const adjustedMin = `${Math.ceil(adjustedMax)}px`;
        items.forEach((item) => {
          if (item.style.minHeight !== adjustedMin) {
            item.style.minHeight = adjustedMin;
          }
        });
      }
    });
  }

  /**
   * @param {Element} container
   */
  function bindContainer(container) {
    const run = () => {
      window.requestAnimationFrame(() => equalize(container));
    };

    run();

    const grid = container.querySelector(':scope > .grid');
    if (!grid) {
      return;
    }

    let resizeTimer;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(run, 120);
    };

    window.addEventListener('resize', onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(onResize);
      observer.observe(grid);
      grid.querySelectorAll(GRID_COLUMN_SELECTOR).forEach((item) => observer.observe(item));
    }

    container.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', run, { once: true });
      }
    });

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(run);
    }
  }

  Drupal.behaviors.globalMakeEvenHeight = {
    attach(context) {
      once('make-even-height', '.make-even-height', context).forEach(bindContainer);
    },
  };
})(Drupal, once);
